import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import SyncIcon from '@mui/icons-material/Sync';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useAuth } from '../features/auth/useAuth';
import { useCurrentPerson } from '../features/users/personUserId';
import { useAppUpdate } from '../features/pwa/appUpdate';
import ConfirmDialog from './ConfirmDialog';
import SettingsDialog from './SettingsDialog';
import DataResyncDialog from './DataResyncDialog';

/**
 * App-bar account menu: an account icon opening a menu of the signed-in user
 * (avatar + name), Einstellungen, Logout and the build version. Renders nothing
 * when logged out. The SAME menu in both apps — the office layout used to spell
 * its name and logout out inline, which meant the two chromes drifted and the
 * language button had to exist twice.
 *
 * Every row is a MenuItem with an icon slot, so they share one type scale and
 * one left edge; the two that aren't actions (user, version) simply don't act.
 */
export default function ProfileMenu() {
    const intl = useIntl();
    const { user, logout } = useAuth();
    /**
     * The app's OWN name for the account (custom name, else the username), the
     * same one every person field, Zeiterfassung row and Verlauf-Eintrag shows.
     * Not the token's displayName: that is the Synology one, which on a bulk
     * import carries the account DESCRIPTION rather than a name — the reason the
     * label resolver skips it everywhere else (see useCurrentPerson).
     */
    const me = useCurrentPerson();
    // A new build waiting to take over: the dot below, and the row under the
    // version. See features/pwa/appUpdate.
    const { updateAvailable, newVersion, applyUpdate } = useAppUpdate();
    const [anchor, setAnchor] = useState<HTMLElement | null>(null);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [resyncOpen, setResyncOpen] = useState(false);
    const [updateOpen, setUpdateOpen] = useState(false);
    // The dot is only worth anything if the button it sits on says what it
    // means — the menu row spells it out, but the button is what gets read out.
    const updateLabel = newVersion
        ? intl.formatMessage(
              { id: 'layout.update.availableVersion' },
              { version: newVersion }
          )
        : intl.formatMessage({ id: 'layout.update.available' });
    const label = updateAvailable
        ? `${intl.formatMessage({ id: 'layout.account' })} — ${updateLabel}`
        : intl.formatMessage({ id: 'layout.account' });

    if (!user) {
        return null;
    }

    // The username until the profiles have replicated — never blank, so the
    // avatar always has its letter.
    const name = me?.name || user.username;
    // Spread, not charAt: an umlaut or a surrogate pair is one letter to a
    // reader, and half a broken character to charAt.
    const initial = ([...name.trim()][0] ?? '').toUpperCase();

    return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip title={label}>
                <IconButton
                    color="inherit"
                    aria-label={label}
                    onClick={(event) => setAnchor(event.currentTarget)}
                >
                    <Badge
                        variant="dot"
                        color="warning"
                        overlap="circular"
                        invisible={!updateAvailable}
                    >
                        <AccountCircleIcon />
                    </Badge>
                </IconButton>
            </Tooltip>
            <Menu
                open={!!anchor}
                anchorEl={anchor}
                onClose={() => setAnchor(null)}
            >
                {/* The signed-in user: a menu ROW, so its type and rhythm match
                    the items below — but it is who you are, not something to do,
                    so tapping it does nothing. */}
                <MenuItem
                    disableRipple
                    sx={{
                        cursor: 'default',
                        '&:hover': { bgcolor: 'transparent' }
                    }}
                >
                    <ListItemIcon>
                        <Avatar
                            sx={{
                                width: 24,
                                height: 24,
                                fontSize: '0.8rem',
                                bgcolor: 'primary.main'
                            }}
                        >
                            {initial}
                        </Avatar>
                    </ListItemIcon>
                    <ListItemText
                        primary={name}
                        secondary={
                            user.username !== name ? user.username : undefined
                        }
                        slotProps={{
                            primary: { noWrap: true },
                            secondary: { noWrap: true }
                        }}
                    />
                </MenuItem>
                <Divider />
                <MenuItem
                    onClick={() => {
                        setAnchor(null);
                        setSettingsOpen(true);
                    }}
                >
                    <ListItemIcon>
                        <SettingsIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>
                        <FormattedMessage id="settings.title" />
                    </ListItemText>
                </MenuItem>
                {/* The repair for a device that is missing something the
                    others can see. Offered to EVERYONE, not just admins: the
                    field tablet is the likeliest to have a gap and the least
                    likely to have anyone to ask (see DataResyncDialog). */}
                <MenuItem
                    onClick={() => {
                        setAnchor(null);
                        setResyncOpen(true);
                    }}
                >
                    <ListItemIcon>
                        <SyncIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>
                        <FormattedMessage id="resync.menu" />
                    </ListItemText>
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        setAnchor(null);
                        logout();
                    }}
                >
                    <ListItemIcon>
                        <LogoutIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>
                        <FormattedMessage id="layout.logout" />
                    </ListItemText>
                </MenuItem>
                <Divider />
                {/* The build version, so a tester can name what they are looking
                    at without opening the settings (where it also stands).
                    Disabled: it is a label that happens to sit in a menu. */}
                <MenuItem disabled>
                    <ListItemIcon>
                        <InfoOutlinedIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>
                        <FormattedMessage
                            id="settings.version"
                            values={{ version: __APP_VERSION__ }}
                        />
                    </ListItemText>
                </MenuItem>
                {/* Directly under the version it answers the question the
                    version raises. Coloured AND bold: it is the one row in this
                    menu that is news rather than a standing option. */}
                {updateAvailable && (
                    <MenuItem
                        onClick={() => {
                            setAnchor(null);
                            setUpdateOpen(true);
                        }}
                        sx={{ color: 'warning.main' }}
                    >
                        <ListItemIcon>
                            <RefreshIcon
                                fontSize="small"
                                sx={{ color: 'warning.main' }}
                            />
                        </ListItemIcon>
                        <ListItemText
                            slotProps={{ primary: { sx: { fontWeight: 600 } } }}
                        >
                            {newVersion ? (
                                <FormattedMessage
                                    id="layout.update.menuVersion"
                                    values={{ version: newVersion }}
                                />
                            ) : (
                                <FormattedMessage id="layout.update.menu" />
                            )}
                        </ListItemText>
                    </MenuItem>
                )}
            </Menu>
            {/* Outside the Menu: they stay open while the menu closes under
                them. */}
            <SettingsDialog
                open={settingsOpen}
                onClose={() => setSettingsOpen(false)}
            />
            <DataResyncDialog
                open={resyncOpen}
                onClose={() => setResyncOpen(false)}
            />
            <ConfirmDialog
                open={updateOpen}
                title={<FormattedMessage id="layout.update.confirmTitle" />}
                message={<FormattedMessage id="layout.update.confirmMessage" />}
                details={
                    <Typography
                        variant="body2"
                        sx={{ mt: 1, color: 'text.secondary' }}
                    >
                        <FormattedMessage
                            id="layout.update.versions"
                            values={{
                                current: __APP_VERSION__,
                                next: newVersion ?? '—'
                            }}
                        />
                    </Typography>
                }
                confirmLabel={<FormattedMessage id="layout.update.confirm" />}
                onConfirm={applyUpdate}
                onCancel={() => setUpdateOpen(false)}
            />
        </Box>
    );
}
